'use client';

import Icon, { Loading3QuartersOutlined, PictureOutlined } from '@ant-design/icons';
import { Alignment, Fit, Layout, useRive } from '@rive-app/react-webgl2';
import { useMemoizedFn } from 'ahooks';
import { Button, Drawer, Input, Modal, Switch, message } from 'antd';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';

import GuideRive from '@/assets/Guide.riv';
import AddSvg from '@/components/Icon/svgs/add.svg';
import PlaySvg from '@/components/Icon/svgs/audio_play.svg';
import CheckSvg from '@/components/Icon/svgs/check_circle.svg';
import RightSvg from '@/components/Icon/svgs/right.svg';
import TalkVoiceSvg from '@/components/Icon/svgs/talk_voice.svg';
import CreateHeader from '@/components/LoginModal/InitPA/CreateHeader';
import PATextArea from '@/components/PATextarea';
import useAppBridge from '@/hooks/useAppBridge';
import useImage2VideoCropper from '@/hooks/useImage2VideoCropper';
import { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
import { useModel } from '@/hooks/useModel';
import useStorage from '@/hooks/useStorage';
import { useTranslation } from '@/i18n/client';
import VoiceSettingsModal from '@/layouts/PA-Layout/VoiceModal';
import { getIdentityOutputList } from '@/service/agent';
import type { IHomepageData } from '@/service/homePage';
import { HOMEPAGE_LAYOUT_TYPE, HOMEPAGE_STATUS, updateHomepageSettings } from '@/service/homePage';
import type { IBriefIntroduction } from '@/service/visualization';
import { useHomePageStore } from '@/store/useHomepageStore';
import useSpaceStore from '@/store/useSpaceStore';
import { defaultHomeLogo } from '@/utils/operation';
import { formatDurationV2 } from '@/utils/timestamp-format';
import { pluralizeWithCountTName } from '@/utils/utils';

import BriefInitTip from './BriefInitTip';
import BriefIntroduction from './BriefIntroduction';
import styles from './index.less';

const defaultCover =
  'https://mindverseglobal.mindverse.com/front-img/crm/1759133775950/20250929-161600.png';

export type GuideType = 'name' | 'bio' | 'cover';

interface IProps {
  className?: string;
  type?: 'edit' | 'init' | 'briefInit' | 'briefEdit' | 'validationEdit';
  handleNextStep?: () => void;
  guideType?: GuideType;
  handleClose?: () => void;
}

const HomepageEditPage = (props: IProps) => {
  const { className, type = 'edit', handleNextStep, guideType, handleClose } = props;
  const [showVoiceIntroModal, setShowVoiceIntroModal] = useState(false);
  const [showBriefInitTip, setShowBriefInitTip] = useState(false);
  const [showSecondMeIdModal, setShowSecondMeIdModal] = useState(false);
  const [nextStepLoading, setNextStepLoading] = useState(false);
  const [layoutSize] = useStorage('layoutSize');
  const inMobile = useMemo(() => layoutSize === LAYOUT_SIZE.SMALL, [layoutSize]);

  const updateSpaceList = useSpaceStore((state) => state.updateSpaceList);

  const homePage = useHomePageStore((state) => state.homePage);
  const fetchHomepage = useHomePageStore((state) => state.fetchHomepage);
  const updateHomepage = useHomePageStore((state) => state.updateHomepage);

  const { userProfile, mindDetail, loadUserDetail } = useModel('auth-model', (model) => ({
    userProfile: model.userProfile,
    mindDetail: model.mindDetail,
    loadUserDetail: model.loadUserDetail
  }));

  const [route, setRoute] = useState<string>(userProfile?.route || '');
  const [name, setName] = useState<string>('');
  const [avatar, setAvatar] = useState<string>('');
  const [cover, setCover] = useState<string>(defaultCover);
  const [video, setVideo] = useState<string>('');
  const [introduction, setIntroduction] = useState<string>('');
  const [briefIntroduction, setBriefIntroduction] = useState<IBriefIntroduction | undefined>(
    undefined
  );
  const [layout, setLayout] = useState<HOMEPAGE_LAYOUT_TYPE>(HOMEPAGE_LAYOUT_TYPE.COVER);
  const [status, setStatus] = useState<HOMEPAGE_STATUS>(HOMEPAGE_STATUS.OPEN);
  const [defaultAutoReply, setDefaultAutoReply] = useState<boolean>(false);
  const [defaultTone, setDefaultTone] = useState<string>('');

  const homepageDataRef = useRef<IHomepageData>(homePage || {});

  const { inApp } = useAppBridge();

  const videoRef = useRef<HTMLVideoElement>(null);
  const layoutContainerRef = useRef<HTMLDivElement>(null);
  const bioInputContainerRef = useRef<HTMLDivElement>(null);

  const [voiceModalOpen, setVoiceModalOpen] = useState<boolean>(false);

  const [nameEdit, setNameEdit] = useState<boolean>(false);
  const [introductionEdit, setIntroductionEdit] = useState<boolean>(false);

  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState<boolean>(false);

  // Voice Intro
  const isVoiceCloned = useMemo(() => {
    return !!mindDetail?.voice?.data?.custom;
  }, [mindDetail]);

  const hasShortBio = useMemo(() => {
    return !!introduction;
  }, [introduction]);

  const showPreCondition = useMemo(() => {
    return !isVoiceCloned || !hasShortBio;
  }, [isVoiceCloned, hasShortBio]);

  const hasCover = useMemo(() => {
    return !!cover && cover !== defaultCover;
  }, [cover]);

  // 判断动画展示位置
  const guidePosition = useMemo<'cover' | 'bio' | 'name' | null>(() => {
    // guideType 决定第一个引导位置（如果该项未完成）
    if (guideType === 'name' && !name) return 'name';

    if (guideType === 'bio' && !introduction) return 'bio';

    if (guideType === 'cover' && !hasCover) return 'cover';

    // briefInit 只引导 cover
    if (type === 'briefInit') {
      if (!hasCover) return 'cover';

      return null;
    }

    // validationEdit 不继续引导了
    if (type === 'validationEdit') return null;

    // 按优先级引导剩余未完成项
    if (!hasCover && !video) return 'cover';

    if (!introduction) return 'bio';

    if (!name) return 'name';

    return null;
  }, [guideType, name, hasCover, video, introduction, type]);

  const canComplete = useMemo(() => {
    return !!name && !!introduction && !!hasCover;
  }, [name, introduction, hasCover]);

  const { FileInput, CropperModal, openCropperModal, resetToCropper } = useImage2VideoCropper({
    handleSetCoverCb: (_cover) => {
      setLayout(HOMEPAGE_LAYOUT_TYPE.COVER);
      setVideo('');
      setCover(_cover);
      homepageDataRef.current.cover = _cover;
      homepageDataRef.current.video = '';
      homepageDataRef.current.layout = HOMEPAGE_LAYOUT_TYPE.COVER;
      handleSave({
        cover: homepageDataRef.current.cover,
        video: homepageDataRef.current.video,
        layout: homepageDataRef.current.layout
      });
    },
    handleSetVideoCb: (url: string) => {
      setLayout(HOMEPAGE_LAYOUT_TYPE.COVER);
      setVideo(url);
      homepageDataRef.current.video = url;
      homepageDataRef.current.layout = HOMEPAGE_LAYOUT_TYPE.COVER;
      handleSave({
        video: homepageDataRef.current.video,
        layout: homepageDataRef.current.layout
      });
    },
    reset: true,
    cropperModalClassName: styles.cropperModal,
    inMobile: inMobile,
    initialCover: cover,
    initialVideo: video
  });

  const { t } = useTranslation();

  // 保存函数 - 支持增量更新
  // changedFields: 指定要更新的字段，如果不传则使用全量更新
  const handleSave = useMemoizedFn((changedFields?: Partial<IHomepageData>) => {
    if (!homepageDataRef.current) {
      return;
    }

    // 验证用户名
    if ('username' in (changedFields || {}) && !homepageDataRef.current.username) {
      message.error('Username cannot be empty');

      return;
    }

    // 验证 defaultTone 长度
    if (homepageDataRef.current.defaultTone && homepageDataRef.current.defaultTone.length > 1000) {
      message.error('Default tone cannot exceed 1000 characters');

      return;
    }

    // 验证 introduction 长度
    if (
      homepageDataRef.current.introduction &&
      homepageDataRef.current.introduction.length > 1000
    ) {
      message.error('Introduction cannot exceed 1000 characters');

      return;
    }

    // 如果传入了 changedFields，只更新指定的字段；否则全量更新
    let data;

    if (changedFields) {
      // 增量更新：只包含变化的字段
      data = { ...changedFields };
    } else {
      // 全量更新：包含所有字段
      data = { ...homepageDataRef.current };
    }

    updateHomepageSettings(data)
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message);
        }

        // TODO: 只在本地进行更新
        if (type === 'init' || type === 'briefInit' || type === 'briefEdit') {
          updateHomepage(homepageDataRef.current);
        }
        // message.success('Save successfully');
      })
      .catch((error) => {
        message.error(error?.message ?? 'Save failed');
      });
  });

  const handleGetData = useMemoizedFn(() => {
    if (!route) return;

    fetchHomepage(route);
  });

  useEffect(() => {
    if (userProfile?.route) {
      setRoute(userProfile.route);
      homepageDataRef.current.route = userProfile.route;
    }

    setAvatar(userProfile?.avatar ?? '');
    setName(userProfile?.name ?? '');
    homepageDataRef.current.avatar = userProfile?.avatar ?? '';
    homepageDataRef.current.username = userProfile?.name ?? '';
  }, [userProfile]);

  useEffect(() => {
    handleGetData();
    loadUserDetail();

    return () => {
      handleGetData();
      loadUserDetail();
    };
  }, []);

  useEffect(() => {
    if (!homePage) return;

    homepageDataRef.current = homePage;

    setAvatar(homePage.avatar ?? '');
    setName(homePage.username ?? '');
    setCover(homePage.cover || defaultCover);
    setVideo(homePage.video ?? '');
    setIntroduction(homePage.introduction ?? '');
    setBriefIntroduction(homePage.briefIntroduction);
    setDefaultTone(homePage.defaultTone ?? '');
    setStatus(homePage.status ?? HOMEPAGE_STATUS.OPEN);
    setDefaultAutoReply(homePage.defaultAutoReply ?? false);
    setLayout(HOMEPAGE_LAYOUT_TYPE.COVER);
  }, [homePage]);

  useEffect(() => {
    getIdentityOutputList()
      .then((res) => {
        if (res.data.code === 0) {
          updateSpaceList(
            res.data.data.list.map((item) => ({
              type: item.outputMode!,
              id: item.id,
              talk: item
            }))
          );
        }
      })
      .catch((error) => {
        console.error('Failed to load space list:', error);
      });
  }, [updateSpaceList]);

  return (
    <div
      className={classNames(styles.container, className)}
      onClick={() => {
        videoRef.current?.play();
      }}
    >
      {type === 'briefInit' ? (
        <CreateHeader
          className="absolute top-[calc(16px+var(--safe-top,0px))] z-[10000] !w-[calc(100%-64px)] max-w-[540px]"
          step={2}
        />
      ) : null}
      {/* background */}
      <div className={styles.backgroundContainer}>
        {inMobile ? (
          layout === HOMEPAGE_LAYOUT_TYPE.COVER && !!video ? (
            <video
              ref={videoRef}
              autoPlay
              className={styles.backgroundImg}
              loop
              muted
              playsInline
              src={video || ''}
              webkit-playsinline="true"
              x5-video-player-type="h5"
            />
          ) : (
            <img
              alt=""
              className={styles.backgroundImg}
              src={
                layout === HOMEPAGE_LAYOUT_TYPE.AVATAR
                  ? avatar || ''
                  : layout === HOMEPAGE_LAYOUT_TYPE.COVER
                    ? cover || ''
                    : ''
              }
            />
          )
        ) : (
          <img
            alt=""
            className={styles.backgroundImg}
            src={
              layout === HOMEPAGE_LAYOUT_TYPE.AVATAR
                ? avatar || ''
                : layout === HOMEPAGE_LAYOUT_TYPE.COVER
                  ? cover || ''
                  : ''
            }
          />
        )}

        {inMobile && layout !== HOMEPAGE_LAYOUT_TYPE.AVATAR ? null : (
          <div className={styles.backgroundImgFilter}>
            {layout === HOMEPAGE_LAYOUT_TYPE.AVATAR && (
              <div className={styles.backgroundAvatar}>
                <img alt="" className={styles.avatar} src={avatar || ''} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* layout */}
      <div
        ref={layoutContainerRef}
        className={classNames(styles.layoutContainer, { [styles.inMobile]: inMobile })}
      >
        {!inMobile && layout === HOMEPAGE_LAYOUT_TYPE.COVER && (
          <div className={styles.backgroundContainer}>
            {video ? (
              <video
                ref={videoRef}
                autoPlay
                className={styles.backgroundImg}
                loop
                muted
                playsInline
                src={video || ''}
                webkit-playsinline="true"
                x5-video-player-type="h5"
              />
            ) : cover ? (
              <img alt="" className={classNames(styles.backgroundImg)} src={cover} />
            ) : (
              <div className="bg-[rgba(254,253,255,70%)] w-full h-full flex items-center justify-center object-cover">
                <PictureOutlined className="text-[40px] text-[#b8ad9b]" />
              </div>
            )}
          </div>
        )}

        {/* Cover */}
        <div
          className={classNames(styles.coverWrapper, 'group')}
          onClick={!resetToCropper ? openCropperModal : undefined}
        >
          <div className="text-base leading-7 text-primary-white transition-all group-hover:[text-shadow:0px_0px_6px_rgba(255,255,255,0.5)]">
            {t('homepage.cover.changeCoverImage')}
          </div>
          {guidePosition === 'cover' && <RiveAnimation size="large" />}
          {FileInput}
        </div>

        {/* Introduction */}
        <div className={classNames(styles.introductionContainer)}>
          <div
            className={classNames(
              'flex flex-col flex-1 text-base gap-[8px] leading-7 w-full text-[#FEFDFF]'
            )}
          >
            <div
              className={classNames(styles.nameEditContainer, 'relative')}
              onClick={() => setNameEdit(true)}
            >
              {`${t('homepage.introduction.hi', {
                username: ''
              })}`}
              {nameEdit ? (
                <input
                  autoFocus
                  className={styles.nameInput}
                  onBlur={() => {
                    setNameEdit(false);
                    handleSave({ username: homepageDataRef.current.username });
                  }}
                  onChange={(e) => {
                    setName(e.target.value);
                    homepageDataRef.current.username = e.target.value;
                  }}
                  value={name}
                />
              ) : (
                <div className={styles.nameDisplay}>{name}</div>
              )}
              {guidePosition === 'name' && <RiveAnimation />}
            </div>

            <div
              ref={bioInputContainerRef}
              className={classNames(styles.bioEditContainer, {
                [styles.editing]: introductionEdit
              })}
              onClick={() => setIntroductionEdit(true)}
            >
              {introductionEdit ? (
                <PATextArea
                  autoFocus
                  autoSize={{ minRows: 2, maxRows: 3 }}
                  className={styles.bioInput}
                  onBlur={() => {
                    setIntroductionEdit(false);
                    handleSave({ introduction: homepageDataRef.current.introduction });
                  }}
                  onChange={(e) => {
                    setIntroduction(e.target.value);
                    homepageDataRef.current.introduction = e.target.value;
                  }}
                  onFocus={(e) => {
                    // 将光标移到文字末尾
                    const length = e.target.value.length;

                    e.target.setSelectionRange(length, length);
                  }}
                  placeholder={t('homepageEdit.shortBio')}
                  value={introduction}
                />
              ) : (
                <div
                  className={classNames(
                    'line-clamp-2 w-full',
                    !introduction ? '!text-[rgba(254,253,255,0.4)]' : '!text-[#fefdff]'
                  )}
                >
                  {introduction || t('homepageEdit.shortBio')}
                </div>
              )}
            </div>
          </div>

          {type === 'briefEdit' || (type === 'briefInit' && inApp) ? null : (
            <div className="flex items-center w-full">
              <div
                className="w-fit rounded-[8px] flex items-center cursor-pointer bg-[rgba(0,0,0,0.15)] py-[4px] pr-[16px] pl-[8px] transition-all hover:bg-[rgba(254,253,255,0.4)]"
                onClick={(e) => {
                  e.stopPropagation();

                  // if (type === 'briefInit') {
                  //   setShowBriefInitTip(true);

                  //   return;
                  // }

                  setShowVoiceIntroModal(true);
                }}
              >
                {briefIntroduction ? (
                  briefIntroduction.voiceUrl ? (
                    <>
                      <Icon className="text-[24px] text-primary-white" component={PlaySvg} />
                      <Icon className="text-[24px] text-primary-white" component={TalkVoiceSvg} />
                      <div className="ml-2 text-sm leading-6 text-primary-white">
                        {formatDurationV2((briefIntroduction.duration || 0) * 1000)}
                      </div>
                    </>
                  ) : (
                    <>
                      <Loading3QuartersOutlined
                        className="text-[16px] p-[4px] text-primary-white"
                        spin
                      />
                      <Icon className="text-[24px] text-primary-white" component={TalkVoiceSvg} />
                      <Icon
                        className="text-[24px] text-primary-white -ml-[8px]"
                        component={TalkVoiceSvg}
                      />
                    </>
                  )
                ) : (
                  <>
                    <Icon className="text-[24px] text-[rgba(254,253,255,0.4)]" component={AddSvg} />
                    <div className="ml-[2px] text-base leading-6 text-[rgba(254,253,255,0.4)]">
                      {t('homepageEdit.introTalk')}
                    </div>
                  </>
                )}
              </div>

              {homePage?.featuredNum ? (
                <div
                  className={styles.showMore}
                  onClick={() => {
                    setShowBriefInitTip(true);
                  }}
                >
                  <div>
                    {t(
                      pluralizeWithCountTName(
                        homePage.featuredNum,
                        'homepage.introduction.featured'
                      ),
                      {
                        count: homePage.featuredNum
                      }
                    )}
                  </div>
                  <Icon className="text-[16px]" component={RightSvg} />
                </div>
              ) : null}
            </div>
          )}

          {guidePosition === 'bio' && <RiveAnimation />}
        </div>

        {/* Advanced Settings */}
        {type === 'edit' && (
          <div
            className={styles.advancedSettingsBtn}
            onClick={() => {
              setAdvancedSettingsOpen(true);
            }}
          >
            <div>{t('homepageEdit.advancedSettings')}</div>
            <Icon className="text-[16px]" component={RightSvg} />
          </div>
        )}

        {type === 'briefInit' && handleNextStep ? (
          <Button
            className={classNames(styles.confirmButton, {
              [styles.canClick]: cover && cover !== defaultCover
            })}
            disabled={!cover || cover === defaultCover}
            loading={nextStepLoading}
            onClick={() => {
              setNextStepLoading(true);
              setTimeout(() => {
                setNextStepLoading(false);
                handleNextStep();
              }, 500);
            }}
          >
            <div className={styles.nextButtonText}>{t('onboarding.create.next')}</div>
          </Button>
        ) : null}

        {(guideType || type === 'briefEdit') && handleClose && (
          <div
            className={classNames(styles.completeButton, {
              [styles.disabled]: !canComplete
            })}
            onClick={() => {
              if (canComplete) {
                handleClose();
              }
            }}
          >
            {t('onboarding.create.done')}
          </div>
        )}
      </div>

      {/* Cover Modal */}
      {CropperModal}

      {/* Advanced Drawer */}
      {type === 'edit' && (
        <Drawer
          destroyOnClose
          getContainer={
            !inMobile && layoutContainerRef.current ? layoutContainerRef.current : undefined
          }
          height={'100%'}
          onClose={() => {
            setAdvancedSettingsOpen(false);
          }}
          open={advancedSettingsOpen}
          placement="bottom"
          rootClassName={styles.drawer}
          title={null}
        >
          <div className={styles.advancedSettingsContainer}>
            {/* header */}
            <div className="w-full flex gap-4 h-9 flex-shrink-0 items-center">
              <div
                className="w-9 h-9 bg-[rgba(254,253,255,0.6)] rounded-[18px] p-1 cursor-pointer hover:bg-[rgba(254,253,255,0.8)] transition-all duration-200"
                onClick={() => {
                  setAdvancedSettingsOpen(false);
                }}
              >
                <Icon
                  className="text-[28px] text-primary-black"
                  component={RightSvg}
                  rotate={180}
                />
              </div>
              <div className="text-[18px] font-medium text-primary-black leading-8">
                {t('homepageEdit.advancedSettings')}
              </div>
            </div>

            <div className="flex gap-6 w-full flex-col overflow-scroll scrollbar-hide pb-[48px]">
              {/* Name*/}
              <div className={styles.section}>
                <div className={styles.title}>{t('homepageEdit.userName')}</div>

                <Input
                  className={styles.advancedInput}
                  onBlur={() => {
                    handleSave({ username: homepageDataRef.current.username });
                  }}
                  onChange={(e) => {
                    setName(e.target.value);
                    homepageDataRef.current.username = e.target.value;
                  }}
                  placeholder={t('homepageEdit.userName')}
                  value={name}
                />
              </div>

              {/* Introduction */}
              <div className={styles.section}>
                <div className={styles.title}>{t('homepageEdit.introduction')}</div>

                <Input.TextArea
                  autoSize={{ minRows: 3, maxRows: 8 }}
                  className={styles.advancedInput}
                  onBlur={() => {
                    handleSave({ introduction: homepageDataRef.current.introduction });
                  }}
                  onChange={(e) => {
                    setIntroduction(e.target.value);
                    homepageDataRef.current.introduction = e.target.value;
                  }}
                  placeholder={t('homepageEdit.shortBio')}
                  value={introduction}
                />
              </div>

              {/* SecondMe ID */}
              <div className={styles.section}>
                <div className={styles.title}>{t('homepageEdit.secondMeId')}</div>
                <div
                  className={styles.HandleIdContainer}
                  onClick={() => setShowSecondMeIdModal(true)}
                >
                  {route}
                  <Icon className="ml-auto text-[24px] text-[#756C5D]" component={RightSvg} />
                </div>
              </div>

              {/* private status */}
              <div className={styles.section}>
                <div className={styles.title}>{t('homepageEdit.privacySettings')}</div>
                <div className={classNames(styles.statusContainer)}>
                  <div>{t('homepageEdit.privateSecondMe')}</div>
                  <Switch
                    checked={status === HOMEPAGE_STATUS.CLOSE}
                    className={styles.switchBtn}
                    onChange={(checked) => {
                      const newStatus = checked ? HOMEPAGE_STATUS.CLOSE : HOMEPAGE_STATUS.OPEN;

                      setStatus(newStatus);
                      homepageDataRef.current.status = newStatus;
                      handleSave({ status: homepageDataRef.current.status });
                    }}
                  />
                </div>
                <div className={styles.description}>
                  {t('homepageEdit.privateSecondMeDescription')}
                </div>
                {status === HOMEPAGE_STATUS.CLOSE ? null : (
                  <>
                    <div className={classNames(styles.statusContainer)}>
                      <div>{t('homepageEdit.autoReplyToStrangers')}</div>
                      <Switch
                        checked={defaultAutoReply}
                        className={styles.switchBtn}
                        onChange={(checked) => {
                          setDefaultAutoReply(checked);
                          homepageDataRef.current.defaultAutoReply = checked;
                          handleSave({
                            defaultAutoReply: homepageDataRef.current.defaultAutoReply
                          });
                        }}
                      />
                    </div>
                    <div className={styles.description}>
                      {t('homepageEdit.autoReplyToStrangersDescription')}
                    </div>
                  </>
                )}
              </div>

              {/* tone */}
              <div className={styles.section}>
                <div className={styles.title}>{t('homepageEdit.defaultSpeakingStyle')}</div>
                <Input.TextArea
                  autoSize={{ minRows: 3, maxRows: 8 }}
                  className={styles.advancedInput}
                  onBlur={() => {
                    handleSave({ defaultTone: homepageDataRef.current.defaultTone });
                  }}
                  onChange={(e) => {
                    setDefaultTone(e.target.value);
                    homepageDataRef.current.defaultTone = e.target.value;
                  }}
                  placeholder={t('homepageEdit.defaultSpeakingStylePlaceholder')}
                  style={{
                    resize: 'none'
                  }}
                  value={defaultTone}
                />
              </div>
            </div>
          </div>

          {/*  Second Me ID Modal */}
          <Modal
            centered
            className={styles.idModal}
            closable={false}
            footer={null}
            onCancel={() => {
              setShowSecondMeIdModal(false);
            }}
            open={showSecondMeIdModal}
          >
            <div className={styles.idModalContainer}>
              <div className="w-full flex flex-col items-center gap-3">
                <div className="text-[24px] leading-[36px] font-[Calistoga-Regular] text-[#231309]">
                  {t('homepageEdit.secondMeId')} : {route}
                </div>
                <div className="text-base leading6 text-[#231309] text-center">
                  {t('homepageEdit.secondMeIdDescription')}
                </div>
              </div>

              <div className="flex flex-col gap-10 items-center w-full">
                <img className="w-[70px] h-[40px] object-cover" src={defaultHomeLogo} />
                <Input
                  className={styles.secondMeIdInput}
                  onBlur={() => {
                    handleSave({ route: homepageDataRef.current.route });
                  }}
                  onChange={(e) => {
                    setRoute(e.target.value);
                    homepageDataRef.current.route = e.target.value;
                  }}
                  placeholder={t('homepageEdit.routePlaceholder')}
                  style={{
                    backgroundColor: 'rgba(254, 253, 255, 50%)'
                  }}
                  value={route}
                />
                <div className="text-[#756C5D] text-center text-sm leading-6">
                  {`${t('homepageEdit.yourHomepageAddressIs')} ${window.location.origin}/${route}`}
                </div>
              </div>

              <div
                className={classNames(styles.secondMeIdBtn, { [styles.disabled]: !route })}
                onClick={() => {
                  if (!route) return;

                  setShowSecondMeIdModal(false);
                }}
              >
                {t('homepageEdit.changeSecondMeId')}
              </div>
            </div>
          </Modal>
        </Drawer>
      )}

      {/* Voice Intro Modal */}
      <Modal
        centered
        className={styles.voiceIntroModal}
        destroyOnClose
        footer={null}
        onCancel={() => {
          setShowVoiceIntroModal(false);
        }}
        open={showVoiceIntroModal}
        title={t('homepageEdit.introTalk')}
        width={580}
      >
        {showPreCondition ? (
          <div className="w-full flex items-center flex-col justify-center gap-8 pt-[24px]">
            <p className="text-[16px] leading-[28px] text-center text-[#5d5446] w-full whitespace-pre-wrap">
              {t('homepageEdit.introTalkModal.tips')}
            </p>
            <div className="flex flex-col gap-4 w-full">
              <div
                className={classNames(
                  'py-[8px] px-4 border border-solid rounded-[24px] flex items-center justify-center gap-[10px] w-full relative shadow-[0px_4px_32px_0px_rgba(194,158,107,0.08)]',
                  hasShortBio
                    ? 'cursor-default text-[#C3BBF4] border-[#C3BBF4]'
                    : 'cursor-pointer border-[#7B67EF] text-[#7B67EF]'
                )}
                onClick={() => {
                  if (hasShortBio) return;

                  setShowVoiceIntroModal(false);
                  setTimeout(() => {
                    bioInputContainerRef.current?.click();
                  }, 500);
                }}
              >
                <div className="text-[14px] leading-[24px] font-medium text-center">
                  {t('homepageEdit.introTalkModal.fillOutShortBio')}
                </div>
                <Icon
                  className="absolute right-[7.5px] top-1/2 -translate-y-1/2 text-[24px]"
                  component={hasShortBio ? CheckSvg : RightSvg}
                />
              </div>
              <div
                className={classNames(
                  'py-[8px] px-4 border border-solid rounded-[24px] flex items-center justify-center gap-[10px] w-full relative shadow-[0px_4px_32px_0px_rgba(194,158,107,0.08)]',
                  isVoiceCloned
                    ? 'cursor-default text-[#C3BBF4] border-[#C3BBF4]'
                    : 'cursor-pointer border-[#7B67EF] text-[#7B67EF]'
                )}
                onClick={() => {
                  if (isVoiceCloned) return;

                  setVoiceModalOpen(true);
                }}
              >
                <div className="text-[14px] leading-[24px] font-medium text-center">
                  {t('homepageEdit.introTalkModal.cloneVoice')}
                </div>
                <Icon
                  className="absolute right-[7.5px] top-1/2 -translate-y-1/2 text-[24px]"
                  component={isVoiceCloned ? CheckSvg : RightSvg}
                />
              </div>
            </div>
          </div>
        ) : (
          <BriefIntroduction
            className="mt-8"
            data={briefIntroduction}
            handleSaveCb={(data) => {
              setBriefIntroduction(data);
              homepageDataRef.current.briefIntroduction = data;
            }}
          />
        )}
      </Modal>

      {/* Voice Modal */}
      <VoiceSettingsModal
        handleClose={() => setVoiceModalOpen(false)}
        handleConfirmCb={() => setVoiceModalOpen(false)}
        open={voiceModalOpen}
      />
      {/* BriefInit 提示 */}
      <BriefInitTip onClose={() => setShowBriefInitTip(false)} open={showBriefInitTip} />
    </div>
  );
};

// Rive 动画组件
const RiveAnimation = ({ size = 'normal' }: { size?: 'normal' | 'large' }) => {
  const { RiveComponent } = useRive(
    {
      // 动画文件路径
      src: GuideRive,
      // 自动播放
      autoplay: true,
      artboard: 'Artboard 2',
      stateMachines: 'State Machine 1',
      layout: new Layout({
        fit: Fit.Contain,
        alignment: Alignment.Center
      })
    },
    {
      fitCanvasToArtboardHeight: true,
      useDevicePixelRatio: true,
      shouldResizeCanvasToContainer: true
    }
  );

  return (
    <RiveComponent className={classNames(styles.guide, { [styles.large]: size === 'large' })} />
  );
};

export default HomepageEditPage;
